import React, { useState, useEffect } from 'react';
import { Button, Row, Col, Form, Spinner } from 'react-bootstrap';
import { PatientServices } from '../../../services/PatientService';

const DiabeticRetinopathyDetailsCard = ({ 
  patientId, 
  patientDetails, 
  hospitalId, 
  showAlert 
}) => {
  const [diabeticRetinopathyData, setDiabeticRetinopathyData] = useState({
    // Test Conducted (multi-select checkboxes)
    testConducted: [], // ['Fundus examination', 'OCT', 'FFA']
    
    // Grading
    npdrGrading: '', // '1', '2', '3', '4'
    pdrGrading: '', // dropdown value
    
    // Treatment Advised (radio buttons)
    treatmentAdvised: '', // 'followUp', 'medication', 'laserRx', 'surgery', 'Intra vitreal injections', 'Patients refused treatment'
    
    // Follow Up fields
    dateOfFollowUp: '', // 'after3Months', 'after6Months', 'after1Year'
    
    // Medication fields
    prescribedMedication: '',
    medicationFollowUpDate: '', // 'after15Days', 'after30Days', 'after90Days'
    followUpHospital: '',
    
    // Laser Rx fields
    laserPrescribedMedication: '',
    dateOfAdmission: '',
    dateOfSurgery: '',
    laserFollowUpDate: '', // 'after15Days', 'after30Days', 'after90Days'
    referredHospital: '',
    insurance: '',
    customInsurance: '', // For when insurance is "Others"
    
    // Surgery fields (same as Laser Rx)
    surgeryPrescribedMedication: '',
    surgeryDateOfAdmission: '',
    surgeryDateOfSurgery: '',
    surgeryFollowUpDate: '', // 'after15Days', 'after30Days', 'after90Days'
    surgeryReferredHospital: '',
    surgeryInsurance: '',
    surgeryCustomInsurance: '', // For when surgery insurance is "Others"
    
    // Intra Vitreal Injections fields
    intraVitrealDateOfAdmission: '',
    intraVitrealFollowUpDate: '', // 'after15Days', 'after30Days', 'after90Days'
    intraVitrealReferredHospital: '',
    intraVitrealInsurance: '',
    intraVitrealCustomInsurance: '', // For when intravitreal insurance is "Others"
    
    // Patients Refused Treatment fields
    reasonForRefusal: '' // 'NCD', 'Other Complications', 'Waiting for other appropriate date'
  });

  const [submittedDiabeticRetinopathyDetails, setSubmittedDiabeticRetinopathyDetails] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);

  useEffect(() => {
    if (patientId) {
      fetchExistingDiabeticRetinopathyDetails();
    }
  }, [patientId]);

  const fetchExistingDiabeticRetinopathyDetails = async () => {
    try {
      const response = await PatientServices.getPatientDiabeticRetinopathyDetailsByPatientId(patientId);
      if (response && response.data) {
        setSubmittedDiabeticRetinopathyDetails(response.data);
      }
    } catch (error) {
      console.error('Error fetching existing diabetic retinopathy details:', error);
      console.error('Error status:', error.response?.status);
      console.error('Error data:', error.response?.data);
      console.error('Error message:', error.message);
    }
  };

  const handleDiabeticRetinopathyDataChange = (field, value) => {
    setDiabeticRetinopathyData(prev => {
      let updated = { ...prev, [field]: value };

      // Handle test conducted checkboxes
      if (field === 'testConducted') {
        const currentTests = prev.testConducted || [];
        if (currentTests.includes(value)) {
          updated.testConducted = currentTests.filter(test => test !== value);
        } else {
          updated.testConducted = [...currentTests, value];
        }
      }

      // Reset fields when treatment advised changes
      if (field === 'treatmentAdvised') {
        updated = {
          ...updated,
          // Reset all treatment-specific fields
          dateOfFollowUp: '',
          prescribedMedication: '',
          medicationFollowUpDate: '',
          followUpHospital: '',
          laserPrescribedMedication: '',
          dateOfAdmission: '',
          dateOfSurgery: '',
          laserFollowUpDate: '',
          referredHospital: '',
          insurance: '',
          customInsurance: '',
          surgeryPrescribedMedication: '',
          surgeryDateOfAdmission: '',
          surgeryDateOfSurgery: '',
          surgeryFollowUpDate: '',
          surgeryReferredHospital: '',
          surgeryInsurance: '',
          surgeryCustomInsurance: '',
          intraVitrealDateOfAdmission: '',
          intraVitrealFollowUpDate: '',
          intraVitrealReferredHospital: '',
          intraVitrealInsurance: '',
          intraVitrealCustomInsurance: '',
          reasonForRefusal: ''
        };
      }

      return updated;
    });
  };

  const handleDiabeticRetinopathySubmit = async () => {
    try {
      setSubmitLoading(true);
      
      // Prepare submit data with proper insurance handling
      const submitData = {
        patientId: patientDetails.id,
        hospitalId: hospitalId,
        diagnosisType: 'diabeticRetinopathy',
        ...diabeticRetinopathyData,
        // Handle custom insurance values
        insurance: diabeticRetinopathyData.insurance === 'Others' 
          ? diabeticRetinopathyData.customInsurance 
          : diabeticRetinopathyData.insurance,
        surgeryInsurance: diabeticRetinopathyData.surgeryInsurance === 'Others' 
          ? diabeticRetinopathyData.surgeryCustomInsurance 
          : diabeticRetinopathyData.surgeryInsurance,
        intraVitrealInsurance: diabeticRetinopathyData.intraVitrealInsurance === 'Others' 
          ? diabeticRetinopathyData.intraVitrealCustomInsurance 
          : diabeticRetinopathyData.intraVitrealInsurance
      };

      console.log('Submitting Diabetic Retinopathy data:', submitData);
      const response = await PatientServices.updatePatientDiabeticRetinopathyDetails(submitData);
      console.log('Diabetic Retinopathy submit response:', response);
      
      // Store the response data for read-only display
      if (response && response.data) {
        if (response.data.data) {
          console.log('Setting submitted diabetic retinopathy details from response.data.data');
          setSubmittedDiabeticRetinopathyDetails(response.data.data);
        } else if (response.data) {
          console.log('Setting submitted diabetic retinopathy details from response.data');
          setSubmittedDiabeticRetinopathyDetails(response.data);
        }
        console.log('Diabetic Retinopathy details saved successfully');
      }
      
      showAlert('Success', 'Diabetic Retinopathy diagnosis updated successfully!', 'success');
    } catch (error) {
      console.error('Error submitting diabetic retinopathy data:', error);
      showAlert('Error', 'Failed to update diabetic retinopathy diagnosis. Please try again.', 'danger');
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <>
      {/* Show form only if no submitted details exist */}
      {!submittedDiabeticRetinopathyDetails && (
        <>
          {/* Test Conducted - Multi-select checkboxes */}
          <Row className="mb-3">
            <Col>
              <Form.Label style={{ fontWeight: 'bold', marginBottom: '10px' }}>Test Conducted :</Form.Label>
              <div className="d-flex flex-wrap gap-3">
                <Form.Check
                  type="checkbox"
                  id="test-fundus"
                  label="Fundus examination"
                  checked={diabeticRetinopathyData.testConducted?.includes('Fundus examination')}
                  onChange={() => handleDiabeticRetinopathyDataChange('testConducted', 'Fundus examination')}
                />
                <Form.Check
                  type="checkbox"
                  id="test-oct"
                  label="OCT"
                  checked={diabeticRetinopathyData.testConducted?.includes('OCT')}
                  onChange={() => handleDiabeticRetinopathyDataChange('testConducted', 'OCT')}
                />
                <Form.Check
                  type="checkbox"
                  id="test-ffa"
                  label="FFA"
                  checked={diabeticRetinopathyData.testConducted?.includes('FFA')}
                  onChange={() => handleDiabeticRetinopathyDataChange('testConducted', 'FFA')}
                />
              </div>
            </Col>
          </Row>

          {/* Grading */}
          <Row className="mb-3">
            <Col md={6}>
              <Form.Label style={{ fontWeight: 'bold' }}>NPDR Grading :</Form.Label>
              <div className="d-flex gap-4 mt-2">
                {['1', '2', '3', '4'].map(grade => (
                  <Form.Check
                    key={grade}
                    type="radio"
                    id={`npdr-${grade}`}
                    name="npdrGrading"
                    label={grade}
                    checked={diabeticRetinopathyData.npdrGrading === grade}
                    onChange={() => handleDiabeticRetinopathyDataChange('npdrGrading', grade)}
                  />
                ))}
              </div>
            </Col>
            <Col md={6}>
              <Form.Label style={{ fontWeight: 'bold' }}>PDR Grading :</Form.Label>
              <Form.Select
                value={diabeticRetinopathyData.pdrGrading}
                onChange={(e) => handleDiabeticRetinopathyDataChange('pdrGrading', e.target.value)}
                className="mt-2"
              >
                <option value="">Select PDR Grading</option>
                <option value="Mild">Mild</option>
                <option value="Moderate">Moderate</option>
                <option value="Severe">Severe</option>
              </Form.Select>
            </Col>
          </Row>

          {/* Treatment Advised */}
          <Row className="mb-3">
            <Col>
              <Form.Label style={{ fontWeight: 'bold', marginBottom: '10px' }}>Treatment Advised :</Form.Label>
              <div className="d-flex flex-wrap gap-4">
                <Form.Check
                  type="radio"
                  id="treatment-followup"
                  name="treatmentAdvised"
                  label="Follow Up"
                  checked={diabeticRetinopathyData.treatmentAdvised === 'followUp'}
                  onChange={() => handleDiabeticRetinopathyDataChange('treatmentAdvised', 'followUp')}
                />
                <Form.Check
                  type="radio"
                  id="treatment-medication"
                  name="treatmentAdvised"
                  label="Medication"
                  checked={diabeticRetinopathyData.treatmentAdvised === 'medication'}
                  onChange={() => handleDiabeticRetinopathyDataChange('treatmentAdvised', 'medication')}
                />
                <Form.Check
                  type="radio"
                  id="treatment-laser"
                  name="treatmentAdvised"
                  label="Laser Rx"
                  checked={diabeticRetinopathyData.treatmentAdvised === 'laserRx'}
                  onChange={() => handleDiabeticRetinopathyDataChange('treatmentAdvised', 'laserRx')}
                />
                <Form.Check
                  type="radio"
                  id="treatment-surgery"
                  name="treatmentAdvised"
                  label="Surgery"
                  checked={diabeticRetinopathyData.treatmentAdvised === 'surgery'}
                  onChange={() => handleDiabeticRetinopathyDataChange('treatmentAdvised', 'surgery')}
                />
                <Form.Check
                  type="radio"
                  id="treatment-intravitreal"
                  name="treatmentAdvised"
                  label="Intra vitreal injections"
                  checked={diabeticRetinopathyData.treatmentAdvised === 'Intra vitreal injections'}
                  onChange={() => handleDiabeticRetinopathyDataChange('treatmentAdvised', 'Intra vitreal injections')}
                />
                <Form.Check
                  type="radio"
                  id="treatment-refused"
                  name="treatmentAdvised"
                  label="Patients refused treatment"
                  checked={diabeticRetinopathyData.treatmentAdvised === 'Patients refused treatment'}
                  onChange={() => handleDiabeticRetinopathyDataChange('treatmentAdvised', 'Patients refused treatment')}
                />
              </div>
            </Col>
          </Row>

          {/* Follow Up Fields */}
          {diabeticRetinopathyData.treatmentAdvised === 'followUp' && (
            <Row className="mb-3">
              <Col md={6}>
                <Form.Label>Date of follow up :</Form.Label>
                <div className="d-flex gap-4 mt-2">
                  <Form.Check
                    type="radio"
                    id="followup-3months"
                    name="dateOfFollowUp"
                    label="After 3 months"
                    checked={diabeticRetinopathyData.dateOfFollowUp === 'after3Months'}
                    onChange={() => handleDiabeticRetinopathyDataChange('dateOfFollowUp', 'after3Months')}
                  />
                  <Form.Check
                    type="radio"
                    id="followup-6months"
                    name="dateOfFollowUp"
                    label="After 6 months"
                    checked={diabeticRetinopathyData.dateOfFollowUp === 'after6Months'}
                    onChange={() => handleDiabeticRetinopathyDataChange('dateOfFollowUp', 'after6Months')}
                  />
                  <Form.Check
                    type="radio"
                    id="followup-1year"
                    name="dateOfFollowUp"
                    label="After 1 year"
                    checked={diabeticRetinopathyData.dateOfFollowUp === 'after1Year'}
                    onChange={() => handleDiabeticRetinopathyDataChange('dateOfFollowUp', 'after1Year')}
                  />
                </div>
              </Col>
            </Row>
          )}

          {/* Medication Fields */}
          {diabeticRetinopathyData.treatmentAdvised === 'medication' && (
            <>
              <Row className="mb-3">
                <Col md={4}>
                  <Form.Label>Prescribed Medication :</Form.Label>
                  <Form.Control
                    type="text"
                    value={diabeticRetinopathyData.prescribedMedication}
                    onChange={(e) => handleDiabeticRetinopathyDataChange('prescribedMedication', e.target.value)}
                    placeholder="Enter medication"
                  />
                </Col>
                <Col md={4}>
                  <Form.Label>Follow Up Hospital :</Form.Label>
                  <Form.Control
                    type="text"
                    value={diabeticRetinopathyData.followUpHospital}
                    onChange={(e) => handleDiabeticRetinopathyDataChange('followUpHospital', e.target.value)}
                    placeholder="Enter hospital name"
                  />
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Label>Follow Up Date :</Form.Label>
                  <div className="d-flex gap-4 mt-2">
                    <Form.Check
                      type="radio"
                      id="med-followup-15days"
                      name="medicationFollowUpDate"
                      label="After 15 days"
                      checked={diabeticRetinopathyData.medicationFollowUpDate === 'after15Days'}
                      onChange={() => handleDiabeticRetinopathyDataChange('medicationFollowUpDate', 'after15Days')}
                    />
                    <Form.Check
                      type="radio"
                      id="med-followup-30days"
                      name="medicationFollowUpDate"
                      label="After 30 days"
                      checked={diabeticRetinopathyData.medicationFollowUpDate === 'after30Days'}
                      onChange={() => handleDiabeticRetinopathyDataChange('medicationFollowUpDate', 'after30Days')}
                    />
                    <Form.Check
                      type="radio"
                      id="med-followup-90days"
                      name="medicationFollowUpDate"
                      label="After 90 days"
                      checked={diabeticRetinopathyData.medicationFollowUpDate === 'after90Days'}
                      onChange={() => handleDiabeticRetinopathyDataChange('medicationFollowUpDate', 'after90Days')}
                    />
                  </div>
                </Col>
              </Row>
            </>
          )}

          {/* Laser Rx Fields */}
          {diabeticRetinopathyData.treatmentAdvised === 'laserRx' && (
            <>
              <Row className="mb-3">
                <Col md={4}>
                  <Form.Label>Prescribed Medication :</Form.Label>
                  <Form.Control
                    type="text"
                    value={diabeticRetinopathyData.laserPrescribedMedication}
                    onChange={(e) => handleDiabeticRetinopathyDataChange('laserPrescribedMedication', e.target.value)}
                    placeholder="Enter medication"
                  />
                </Col>
                <Col md={4}>
                  <Form.Label>Date of Admission :</Form.Label>
                  <Form.Control
                    type="date"
                    value={diabeticRetinopathyData.dateOfAdmission}
                    onChange={(e) => handleDiabeticRetinopathyDataChange('dateOfAdmission', e.target.value)}
                  />
                </Col>
                <Col md={4}>
                  <Form.Label>Date of Surgery :</Form.Label>
                  <Form.Control
                    type="date"
                    value={diabeticRetinopathyData.dateOfSurgery}
                    onChange={(e) => handleDiabeticRetinopathyDataChange('dateOfSurgery', e.target.value)}
                  />
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={4}>
                  <Form.Label>Referred Hospital :</Form.Label>
                  <Form.Control
                    type="text"
                    value={diabeticRetinopathyData.referredHospital}
                    onChange={(e) => handleDiabeticRetinopathyDataChange('referredHospital', e.target.value)}
                    placeholder="Enter hospital name"
                  />
                </Col>
                <Col md={4}>
                  <Form.Label>Insurance :</Form.Label>
                  <Form.Select
                    value={diabeticRetinopathyData.insurance}
                    onChange={(e) => handleDiabeticRetinopathyDataChange('insurance', e.target.value)}
                  >
                    <option value="">Select Insurance</option>
                    <option value="BPL">BPL</option>
                    <option value="Yashaswini">Yashaswini</option>
                    <option value="ESI">ESI</option>
                    <option value="Dharmastala">Dharmastala</option>
                    <option value="BCS">BCS</option>
                    <option value="Others">Others</option>
                  </Form.Select>
                </Col>
                {diabeticRetinopathyData.insurance === 'Others' && (
                  <Col md={4}>
                    <Form.Label>Specify Insurance :</Form.Label>
                    <Form.Control
                      type="text"
                      value={diabeticRetinopathyData.customInsurance}
                      onChange={(e) => handleDiabeticRetinopathyDataChange('customInsurance', e.target.value)}
                      placeholder="Enter insurance details"
                    />
                  </Col>
                )}
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Label>Follow Up Date :</Form.Label>
                  <div className="d-flex gap-4 mt-2">
                    <Form.Check
                      type="radio"
                      id="laser-followup-15days"
                      name="laserFollowUpDate"
                      label="After 15 days"
                      checked={diabeticRetinopathyData.laserFollowUpDate === 'after15Days'}
                      onChange={() => handleDiabeticRetinopathyDataChange('laserFollowUpDate', 'after15Days')}
                    />
                    <Form.Check
                      type="radio"
                      id="laser-followup-30days"
                      name="laserFollowUpDate"
                      label="After 30 days"
                      checked={diabeticRetinopathyData.laserFollowUpDate === 'after30Days'}
                      onChange={() => handleDiabeticRetinopathyDataChange('laserFollowUpDate', 'after30Days')}
                    />
                    <Form.Check
                      type="radio"
                      id="laser-followup-90days"
                      name="laserFollowUpDate"
                      label="After 90 days"
                      checked={diabeticRetinopathyData.laserFollowUpDate === 'after90Days'}
                      onChange={() => handleDiabeticRetinopathyDataChange('laserFollowUpDate', 'after90Days')}
                    />
                  </div>
                </Col>
              </Row>
            </>
          )}

          {/* Surgery Fields */}
          {diabeticRetinopathyData.treatmentAdvised === 'surgery' && (
            <>
              <Row className="mb-3">
                <Col md={4}>
                  <Form.Label>Prescribed Medication :</Form.Label>
                  <Form.Control
                    type="text"
                    value={diabeticRetinopathyData.surgeryPrescribedMedication}
                    onChange={(e) => handleDiabeticRetinopathyDataChange('surgeryPrescribedMedication', e.target.value)}
                    placeholder="Enter medication"
                  />
                </Col>
                <Col md={4}>
                  <Form.Label>Date of Admission :</Form.Label>
                  <Form.Control
                    type="date"
                    value={diabeticRetinopathyData.surgeryDateOfAdmission}
                    onChange={(e) => handleDiabeticRetinopathyDataChange('surgeryDateOfAdmission', e.target.value)}
                  />
                </Col>
                <Col md={4}>
                  <Form.Label>Date of Surgery :</Form.Label>
                  <Form.Control
                    type="date"
                    value={diabeticRetinopathyData.surgeryDateOfSurgery}
                    onChange={(e) => handleDiabeticRetinopathyDataChange('surgeryDateOfSurgery', e.target.value)}
                  />
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={4}>
                  <Form.Label>Referred Hospital :</Form.Label>
                  <Form.Control
                    type="text"
                    value={diabeticRetinopathyData.surgeryReferredHospital}
                    onChange={(e) => handleDiabeticRetinopathyDataChange('surgeryReferredHospital', e.target.value)}
                    placeholder="Enter hospital name"
                  />
                </Col>
                <Col md={4}>
                  <Form.Label>Insurance :</Form.Label>
                  <Form.Select
                    value={diabeticRetinopathyData.surgeryInsurance}
                    onChange={(e) => handleDiabeticRetinopathyDataChange('surgeryInsurance', e.target.value)}
                  >
                    <option value="">Select Insurance</option>
                    <option value="BPL">BPL</option>
                    <option value="Yashaswini">Yashaswini</option>
                    <option value="ESI">ESI</option>
                    <option value="Dharmastala">Dharmastala</option>
                    <option value="BCS">BCS</option>
                    <option value="Others">Others</option>
                  </Form.Select>
                </Col>
                {diabeticRetinopathyData.surgeryInsurance === 'Others' && (
                  <Col md={4}>
                    <Form.Label>Specify Insurance :</Form.Label>
                    <Form.Control
                      type="text"
                      value={diabeticRetinopathyData.surgeryCustomInsurance}
                      onChange={(e) => handleDiabeticRetinopathyDataChange('surgeryCustomInsurance', e.target.value)}
                      placeholder="Enter insurance details"
                    />
                  </Col>
                )}
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Label>Follow Up Date :</Form.Label>
                  <div className="d-flex gap-4 mt-2">
                    <Form.Check
                      type="radio"
                      id="surgery-followup-15days"
                      name="surgeryFollowUpDate"
                      label="After 15 days"
                      checked={diabeticRetinopathyData.surgeryFollowUpDate === 'after15Days'}
                      onChange={() => handleDiabeticRetinopathyDataChange('surgeryFollowUpDate', 'after15Days')}
                    />
                    <Form.Check
                      type="radio"
                      id="surgery-followup-30days"
                      name="surgeryFollowUpDate"
                      label="After 30 days"
                      checked={diabeticRetinopathyData.surgeryFollowUpDate === 'after30Days'}
                      onChange={() => handleDiabeticRetinopathyDataChange('surgeryFollowUpDate', 'after30Days')}
                    />
                    <Form.Check
                      type="radio"
                      id="surgery-followup-90days"
                      name="surgeryFollowUpDate"
                      label="After 90 days"
                      checked={diabeticRetinopathyData.surgeryFollowUpDate === 'after90Days'}
                      onChange={() => handleDiabeticRetinopathyDataChange('surgeryFollowUpDate', 'after90Days')}
                    />
                  </div>
                </Col>
              </Row>
            </>
          )}

          {/* Intra Vitreal Injections Fields */}
          {diabeticRetinopathyData.treatmentAdvised === 'Intra vitreal injections' && (
            <>
              <Row className="mb-3">
                <Col md={4}>
                  <Form.Label>Date of Admission :</Form.Label>
                  <Form.Control
                    type="date"
                    value={diabeticRetinopathyData.intraVitrealDateOfAdmission}
                    onChange={(e) => handleDiabeticRetinopathyDataChange('intraVitrealDateOfAdmission', e.target.value)}
                  />
                </Col>
                <Col md={4}>
                  <Form.Label>Referred Hospital :</Form.Label>
                  <Form.Control
                    type="text"
                    value={diabeticRetinopathyData.intraVitrealReferredHospital}
                    onChange={(e) => handleDiabeticRetinopathyDataChange('intraVitrealReferredHospital', e.target.value)}
                    placeholder="Enter hospital name"
                  />
                </Col>
                <Col md={4}>
                  <Form.Label>Insurance :</Form.Label>
                  <Form.Select
                    value={diabeticRetinopathyData.intraVitrealInsurance}
                    onChange={(e) => handleDiabeticRetinopathyDataChange('intraVitrealInsurance', e.target.value)}
                  >
                    <option value="">Select Insurance</option>
                    <option value="BPL">BPL</option>
                    <option value="Yashaswini">Yashaswini</option>
                    <option value="ESI">ESI</option>
                    <option value="Dharmastala">Dharmastala</option>
                    <option value="BCS">BCS</option>
                    <option value="Others">Others</option>
                  </Form.Select>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Label>Follow Up Date :</Form.Label>
                  <div className="d-flex gap-4 mt-2">
                    <Form.Check
                      type="radio"
                      id="intravitreal-followup-15days"
                      name="intraVitrealFollowUpDate"
                      label="After 15 days"
                      checked={diabeticRetinopathyData.intraVitrealFollowUpDate === 'after15Days'}
                      onChange={() => handleDiabeticRetinopathyDataChange('intraVitrealFollowUpDate', 'after15Days')}
                    />
                    <Form.Check
                      type="radio"
                      id="intravitreal-followup-30days"
                      name="intraVitrealFollowUpDate"
                      label="After 30 days"
                      checked={diabeticRetinopathyData.intraVitrealFollowUpDate === 'after30Days'}
                      onChange={() => handleDiabeticRetinopathyDataChange('intraVitrealFollowUpDate', 'after30Days')}
                    />
                    <Form.Check
                      type="radio"
                      id="intravitreal-followup-90days"
                      name="intraVitrealFollowUpDate"
                      label="After 90 days"
                      checked={diabeticRetinopathyData.intraVitrealFollowUpDate === 'after90Days'}
                      onChange={() => handleDiabeticRetinopathyDataChange('intraVitrealFollowUpDate', 'after90Days')}
                    />
                  </div>
                </Col>
                {diabeticRetinopathyData.intraVitrealInsurance === 'Others' && (
                  <Col md={4}>
                    <Form.Label>Specify Insurance :</Form.Label>
                    <Form.Control
                      type="text"
                      value={diabeticRetinopathyData.intraVitrealCustomInsurance}
                      onChange={(e) => handleDiabeticRetinopathyDataChange('intraVitrealCustomInsurance', e.target.value)}
                      placeholder="Enter insurance details"
                    />
                  </Col>
                )}
              </Row>
            </>
          )}

          {/* Patients Refused Treatment Fields */}
          {diabeticRetinopathyData.treatmentAdvised === 'Patients refused treatment' && (
            <Row className="mb-3">
              <Col md={6}>
                <Form.Label>Reason for refusal :</Form.Label>
                <Form.Select
                  value={diabeticRetinopathyData.reasonForRefusal}
                  onChange={(e) => handleDiabeticRetinopathyDataChange('reasonForRefusal', e.target.value)}
                >
                  <option value="">Select Reason</option>
                  <option value="NCD">NCD</option>
                  <option value="Other Complications">Other Complications</option>
                  <option value="Waiting for other appropriate date">Waiting for other appropriate date</option>
                </Form.Select>
              </Col>
            </Row>
          )}
      
          {/* Submit Button for Diabetic Retinopathy */}
          <Row className="mt-4">
            <Col className="d-flex justify-content-end">
              <Button 
                variant="success" 
                onClick={handleDiabeticRetinopathySubmit}
                disabled={submitLoading || !diabeticRetinopathyData.treatmentAdvised}
                style={{ 
                  padding: '0.5rem 2rem',
                  fontSize: '1rem'
                }}
              >
                {submitLoading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Submitting...
                  </>
                ) : (
                  'Submit'
                )}
              </Button>
            </Col>
          </Row>
        </>
      )}

      {/* Read-only Display of Submitted Diabetic Retinopathy Details */}
      {submittedDiabeticRetinopathyDetails && (
        <>
          {/* Basic Information */}
          <Row className="mb-3">
            <Col md={4}>
              <Form.Label style={{ fontWeight: 'bold' }}>Test Conducted:</Form.Label>
              <Form.Control
                type="text"
                value={Array.isArray(submittedDiabeticRetinopathyDetails.testConducted) 
                  ? submittedDiabeticRetinopathyDetails.testConducted.join(', ') 
                  : submittedDiabeticRetinopathyDetails.testConducted || 'N/A'}
                readOnly
                style={{ backgroundColor: '#f8f9fa' }}
              />
            </Col>
            <Col md={4}>
              <Form.Label style={{ fontWeight: 'bold' }}>NPDR Grading:</Form.Label>
              <Form.Control
                type="text"
                value={submittedDiabeticRetinopathyDetails.npdrGrading || 'N/A'}
                readOnly
                style={{ backgroundColor: '#f8f9fa' }}
              />
            </Col>
            <Col md={4}>
              <Form.Label style={{ fontWeight: 'bold' }}>PDR Grading:</Form.Label>
              <Form.Control
                type="text"
                value={submittedDiabeticRetinopathyDetails.pdrGrading || 'N/A'}
                readOnly
                style={{ backgroundColor: '#f8f9fa' }}
              />
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={4}>
              <Form.Label style={{ fontWeight: 'bold' }}>Treatment Advised:</Form.Label>
              <Form.Control
                type="text"
                value={submittedDiabeticRetinopathyDetails.treatmentAdvised || 'N/A'}
                readOnly
                style={{ backgroundColor: '#f8f9fa' }}
              />
            </Col>
          </Row>

          {/* Treatment-specific read-only fields */}
          {submittedDiabeticRetinopathyDetails.treatmentAdvised === 'followUp' && (
            <Row className="mb-3">
              <Col md={4}>
                <Form.Label style={{ fontWeight: 'bold' }}>Date of Follow Up:</Form.Label>
                <Form.Control
                  type="text"
                  value={submittedDiabeticRetinopathyDetails.dateOfFollowUp || 'N/A'}
                  readOnly
                  style={{ backgroundColor: '#f8f9fa' }}
                />
              </Col>
            </Row>
          )}

          {submittedDiabeticRetinopathyDetails.treatmentAdvised === 'medication' && (
            <Row className="mb-3">
              <Col md={4}>
                <Form.Label style={{ fontWeight: 'bold' }}>Prescribed Medication:</Form.Label>
                <Form.Control
                  type="text"
                  value={submittedDiabeticRetinopathyDetails.prescribedMedication || 'N/A'}
                  readOnly
                  style={{ backgroundColor: '#f8f9fa' }}
                />
              </Col>
              <Col md={4}>
                <Form.Label style={{ fontWeight: 'bold' }}>Follow Up Hospital:</Form.Label>
                <Form.Control
                  type="text"
                  value={submittedDiabeticRetinopathyDetails.followUpHospital || 'N/A'}
                  readOnly
                  style={{ backgroundColor: '#f8f9fa' }}
                />
              </Col>
              <Col md={4}>
                <Form.Label style={{ fontWeight: 'bold' }}>Follow Up Date:</Form.Label>
                <Form.Control
                  type="text"
                  value={submittedDiabeticRetinopathyDetails.medicationFollowUpDate || 'N/A'}
                  readOnly
                  style={{ backgroundColor: '#f8f9fa' }}
                />
              </Col>
            </Row>
          )}

          {submittedDiabeticRetinopathyDetails.treatmentAdvised === 'laserRx' && (
            <>
              <Row className="mb-3">
                <Col md={4}>
                  <Form.Label style={{ fontWeight: 'bold' }}>Prescribed Medication:</Form.Label>
                  <Form.Control
                    type="text"
                    value={submittedDiabeticRetinopathyDetails.laserPrescribedMedication || 'N/A'}
                    readOnly
                    style={{ backgroundColor: '#f8f9fa' }}
                  />
                </Col>
                <Col md={4}>
                  <Form.Label style={{ fontWeight: 'bold' }}>Date of Admission:</Form.Label>
                  <Form.Control
                    type="text"
                    value={submittedDiabeticRetinopathyDetails.dateOfAdmission || 'N/A'}
                    readOnly
                    style={{ backgroundColor: '#f8f9fa' }}
                  />
                </Col>
                <Col md={4}>
                  <Form.Label style={{ fontWeight: 'bold' }}>Date of Surgery:</Form.Label>
                  <Form.Control
                    type="text"
                    value={submittedDiabeticRetinopathyDetails.dateOfSurgery || 'N/A'}
                    readOnly
                    style={{ backgroundColor: '#f8f9fa' }}
                  />
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={4}>
                  <Form.Label style={{ fontWeight: 'bold' }}>Referred Hospital:</Form.Label>
                  <Form.Control
                    type="text"
                    value={submittedDiabeticRetinopathyDetails.referredHospital || 'N/A'}
                    readOnly
                    style={{ backgroundColor: '#f8f9fa' }}
                  />
                </Col>
                <Col md={4}>
                  <Form.Label style={{ fontWeight: 'bold' }}>Insurance:</Form.Label>
                  <Form.Control
                    type="text"
                    value={submittedDiabeticRetinopathyDetails.insurance || 'N/A'}
                    readOnly
                    style={{ backgroundColor: '#f8f9fa' }}
                  />
                </Col>
                <Col md={4}>
                  <Form.Label style={{ fontWeight: 'bold' }}>Follow Up Date:</Form.Label>
                  <Form.Control
                    type="text"
                    value={submittedDiabeticRetinopathyDetails.laserFollowUpDate || 'N/A'}
                    readOnly
                    style={{ backgroundColor: '#f8f9fa' }}
                  />
                </Col>
              </Row>
            </>
          )}

          {submittedDiabeticRetinopathyDetails.treatmentAdvised === 'surgery' && (
            <>
              <Row className="mb-3">
                <Col md={4}>
                  <Form.Label style={{ fontWeight: 'bold' }}>Prescribed Medication:</Form.Label>
                  <Form.Control
                    type="text"
                    value={submittedDiabeticRetinopathyDetails.surgeryPrescribedMedication || 'N/A'}
                    readOnly
                    style={{ backgroundColor: '#f8f9fa' }}
                  />
                </Col>
                <Col md={4}>
                  <Form.Label style={{ fontWeight: 'bold' }}>Date of Admission:</Form.Label>
                  <Form.Control
                    type="text"
                    value={submittedDiabeticRetinopathyDetails.surgeryDateOfAdmission || 'N/A'}
                    readOnly
                    style={{ backgroundColor: '#f8f9fa' }}
                  />
                </Col>
                <Col md={4}>
                  <Form.Label style={{ fontWeight: 'bold' }}>Date of Surgery:</Form.Label>
                  <Form.Control
                    type="text"
                    value={submittedDiabeticRetinopathyDetails.surgeryDateOfSurgery || 'N/A'}
                    readOnly
                    style={{ backgroundColor: '#f8f9fa' }}
                  />
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={4}>
                  <Form.Label style={{ fontWeight: 'bold' }}>Referred Hospital:</Form.Label>
                  <Form.Control
                    type="text"
                    value={submittedDiabeticRetinopathyDetails.surgeryReferredHospital || 'N/A'}
                    readOnly
                    style={{ backgroundColor: '#f8f9fa' }}
                  />
                </Col>
                <Col md={4}>
                  <Form.Label style={{ fontWeight: 'bold' }}>Insurance:</Form.Label>
                  <Form.Control
                    type="text"
                    value={submittedDiabeticRetinopathyDetails.surgeryInsurance || 'N/A'}
                    readOnly
                    style={{ backgroundColor: '#f8f9fa' }}
                  />
                </Col>
                <Col md={4}>
                  <Form.Label style={{ fontWeight: 'bold' }}>Follow Up Date:</Form.Label>
                  <Form.Control
                    type="text"
                    value={submittedDiabeticRetinopathyDetails.surgeryFollowUpDate || 'N/A'}
                    readOnly
                    style={{ backgroundColor: '#f8f9fa' }}
                  />
                </Col>
              </Row>
            </>
          )}

          {submittedDiabeticRetinopathyDetails.treatmentAdvised === 'Intra vitreal injections' && (
            <Row className="mb-3">
              <Col md={4}>
                <Form.Label style={{ fontWeight: 'bold' }}>Date of Admission:</Form.Label>
                <Form.Control
                  type="text"
                  value={submittedDiabeticRetinopathyDetails.intraVitrealDateOfAdmission || 'N/A'}
                  readOnly
                  style={{ backgroundColor: '#f8f9fa' }}
                />
              </Col>
              <Col md={4}>
                <Form.Label style={{ fontWeight: 'bold' }}>Referred Hospital:</Form.Label>
                <Form.Control
                  type="text"
                  value={submittedDiabeticRetinopathyDetails.intraVitrealReferredHospital || 'N/A'}
                  readOnly
                  style={{ backgroundColor: '#f8f9fa' }}
                />
              </Col>
              <Col md={4}>
                <Form.Label style={{ fontWeight: 'bold' }}>Insurance:</Form.Label>
                <Form.Control
                  type="text"
                  value={submittedDiabeticRetinopathyDetails.intraVitrealInsurance || 'N/A'}
                  readOnly
                  style={{ backgroundColor: '#f8f9fa' }}
                />
              </Col>
            </Row>
          )}

          {submittedDiabeticRetinopathyDetails.treatmentAdvised === 'Patients refused treatment' && (
            <Row className="mb-3">
              <Col md={4}>
                <Form.Label style={{ fontWeight: 'bold' }}>Reason for Refusal:</Form.Label>
                <Form.Control
                  type="text"
                  value={submittedDiabeticRetinopathyDetails.reasonForRefusal || 'N/A'}
                  readOnly
                  style={{ backgroundColor: '#f8f9fa' }}
                />
              </Col>
            </Row>
          )}
        </>
      )}
    </>
  );
};

export default DiabeticRetinopathyDetailsCard;
